import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getSWAEventsList,
  saveSWAEventRow,
  deleteSWAEventRow,
  getActivitiesList,
  getJSAEvaluationsList,
  type SWAEventRow,
  type SWASeverity,
  type SWAWorkStatus,
  type ActivityRow,
  type JSAEvaluationRow,
} from '@/services/db/dsrEntry';
import PhotoUploader from '@/components/dsr/PhotoUploader';
import CreateActionItemButton from './CreateActionItemButton';

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
}
import {
  FieldLabel,
  FieldError,
  ChipGroup,
  MultiChipGroup,
  formatDateTimeLocal,
  fromDateTimeLocal,
} from './_shared';

const SEVERITY_OPTIONS: { value: SWASeverity; label: string; color: string }[] = [
  { value: 'minor', label: 'Minor', color: '#00b4b4' },
  { value: 'moderate', label: 'Moderate', color: '#1668a0' },
  { value: 'serious', label: 'Serious', color: '#d87820' },
  { value: 'critical', label: 'Critical', color: '#c0392b' },
];

const CATEGORY_OPTIONS = [
  { value: 'personnel', label: 'Personnel' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'environment', label: 'Environment' },
  { value: 'process', label: 'Process' },
  { value: 'organizational', label: 'Organizational' },
  { value: 'external', label: 'External' },
] as const;

const WORK_STATUS_OPTIONS: { value: SWAWorkStatus; label: string; color: string }[] = [
  { value: 'stopped', label: 'Stopped', color: '#c0392b' },
  { value: 'resumed', label: 'Resumed', color: '#3ba870' },
  { value: 'ongoing', label: 'Ongoing', color: '#d87820' },
];

const entrySchema = z
  .object({
    invoked_at: z.string().min(1, 'Event time is required'),
    site_location: z.string().min(1, 'Site location is required').max(200),
    activity_description: z.string().min(1, 'Activity description is required').max(500),
    activity_entry_id: z.string().optional(),
    invoked_by: z.string().min(1, 'Initiated by is required'),
    hazard_description: z.string().min(1, 'Safety concern is required').max(500),
    action_taken: z.string().min(1, 'Immediate actions is required').max(500),
    work_crew: z.string().optional(),
    severity: z.string().min(1, 'Severity is required'),
    category: z.array(z.string()).min(1, 'At least one category is required'),
    personnel_count: z.string().optional(),
    is_supervisor_notified: z.boolean(),
    supervisor_name: z.string().optional(),
    is_management_notified: z.boolean(),
    work_status: z.string().min(1, 'Work status is required'),
    outcome: z.string().max(500).optional(),
    authorized_resumption_by: z.string().optional(),
    resumption_time: z.string().optional(),
    lessons_learned: z.string().max(500).optional(),
    jsa_evaluation_id: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.work_status === 'resumed') {
      if (!data.outcome?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Resolution description is required when work is resumed',
          path: ['outcome'],
        });
      }
      if (!data.authorized_resumption_by?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Authorized by is required when work is resumed',
          path: ['authorized_resumption_by'],
        });
      }
      if (!data.resumption_time?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Resumption time is required when work is resumed',
          path: ['resumption_time'],
        });
      }
    }
  });

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  invoked_at: formatDateTimeLocal(new Date().toISOString()),
  site_location: '',
  activity_description: '',
  activity_entry_id: '',
  invoked_by: '',
  hazard_description: '',
  action_taken: '',
  work_crew: '',
  severity: 'minor',
  category: ['personnel'],
  personnel_count: '',
  is_supervisor_notified: false,
  supervisor_name: '',
  is_management_notified: false,
  work_status: 'stopped',
  outcome: '',
  authorized_resumption_by: '',
  resumption_time: '',
  lessons_learned: '',
  jsa_evaluation_id: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function SWAForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: SWAEventRow }>('closed');

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_swa', dsrId],
    queryFn: () => getSWAEventsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: jsaEvaluations = [] } = useQuery({
    queryKey: ['entry_jsa_evaluations', dsrId],
    queryFn: () => getJSAEvaluationsList(dsrId),
    staleTime: 30 * 1000,
  });

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_swa', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this SWA event?')) return;
    try {
      await deleteSWAEventRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_swa', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete event');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Stop Work Authority</h2>
        <p className="text-xs text-muted-foreground">
          Document any work stoppage initiated under SWA today.
        </p>
      </div>

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No SWA events recorded. Add one below if work was stopped under SWA today.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <SWACard
          key={entry.id}
          entry={entry}
          dsrId={dsrId}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <SWAEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          jsaEvaluations={jsaEvaluations}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add SWA Event
        </Button>
      )}
    </div>
  );
}

function SWACard({
  entry,
  dsrId,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: SWAEventRow;
  dsrId: string;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const sev = SEVERITY_OPTIONS.find((o) => o.value === entry.severity);
  const ws = WORK_STATUS_OPTIONS.find((o) => o.value === entry.work_status);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex flex-wrap gap-2">
          {sev && (
            <Badge style={{ backgroundColor: sev.color }} className="text-white">
              {sev.label.toUpperCase()}
            </Badge>
          )}
          {ws && (
            <Badge style={{ backgroundColor: ws.color }} className="text-white">
              {ws.label.toUpperCase()}
            </Badge>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {new Date(entry.invoked_at).toLocaleString()}
          </span>
        </div>
        <h3 className="font-semibold text-mozaic-navy">{entry.site_location}</h3>
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
          {entry.hazard_description}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">Initiated by: {entry.invoked_by}</p>
        <PhotoUploader
          entityType="swa_event"
          entityId={entry.id}
          dsrId={dsrId}
          readOnly={isReadOnly}
        />
        {!isReadOnly && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
            <div className="ml-auto">
              <CreateActionItemButton
                dsrId={dsrId}
                sourceSection="swa_events"
                defaultDescription={`SWA follow-up: ${truncate(entry.activity_description, 100)}`}
                defaultPriority="high"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SWAEntryForm({
  dsrId,
  tenantId,
  activities,
  jsaEvaluations,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  jsaEvaluations: JSAEvaluationRow[];
  editing?: SWAEventRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const hasActivities = activities.length > 0;
  const hasJSAs = jsaEvaluations.length > 0;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          invoked_at: formatDateTimeLocal(editing.invoked_at),
          site_location: editing.site_location,
          activity_description: editing.activity_description,
          activity_entry_id: editing.activity_entry_id ?? '',
          invoked_by: editing.invoked_by,
          hazard_description: editing.hazard_description,
          action_taken: editing.action_taken,
          work_crew: editing.work_crew ?? '',
          severity: editing.severity,
          category: editing.category ?? ['personnel'],
          personnel_count: editing.personnel_count?.toString() ?? '',
          is_supervisor_notified: editing.is_supervisor_notified,
          supervisor_name: editing.supervisor_name ?? '',
          is_management_notified: editing.is_management_notified,
          work_status: editing.work_status,
          outcome: editing.outcome ?? '',
          authorized_resumption_by: editing.authorized_resumption_by ?? '',
          resumption_time: formatDateTimeLocal(editing.resumption_time),
          lessons_learned: editing.lessons_learned ?? '',
          jsa_evaluation_id: editing.jsa_evaluation_id ?? '',
        }
      : EMPTY,
  });

  const workStatus = watch('work_status');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveSWAEventRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        invokedAt: fromDateTimeLocal(values.invoked_at),
        siteLocation: values.site_location,
        activityDescription: values.activity_description,
        activityEntryId:
          hasActivities && values.activity_entry_id ? values.activity_entry_id : null,
        invokedBy: values.invoked_by,
        hazardDescription: values.hazard_description,
        actionTaken: values.action_taken,
        workCrew: values.work_crew || null,
        severity: values.severity as SWASeverity,
        category: values.category,
        personnelCount: values.personnel_count ? parseInt(values.personnel_count, 10) : null,
        isSupervisorNotified: values.is_supervisor_notified,
        supervisorName: values.supervisor_name || null,
        isManagementNotified: values.is_management_notified,
        workStatus: values.work_status as SWAWorkStatus,
        outcome: values.outcome || null,
        authorizedResumptionBy: values.authorized_resumption_by || null,
        resumptionTime: values.resumption_time ? fromDateTimeLocal(values.resumption_time) : null,
        lessonsLearned: values.lessons_learned || null,
        jsaEvaluationId: hasJSAs && values.jsa_evaluation_id ? values.jsa_evaluation_id : null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save SWA event');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit SWA Event' : 'New SWA Event'}</h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel required>Event Date / Time</FieldLabel>
            <Controller
              control={control}
              name="invoked_at"
              render={({ field }) => <Input {...field} type="datetime-local" />}
            />
            <FieldError message={errors.invoked_at?.message} />
          </div>
          <div className="space-y-2">
            <FieldLabel required>Site Location</FieldLabel>
            <Controller
              control={control}
              name="site_location"
              render={({ field }) => (
                <Input {...field} maxLength={200} placeholder="Where on the site?" />
              )}
            />
            <FieldError message={errors.site_location?.message} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel required>Activity Description</FieldLabel>
          <Controller
            control={control}
            name="activity_description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={2}
                maxLength={500}
                placeholder="What work was being performed?"
              />
            )}
          />
          <FieldError message={errors.activity_description?.message} />
        </div>

        {hasActivities && (
          <div className="space-y-2">
            <FieldLabel>Linked Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_entry_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...activities.map((a) => ({
                      value: a.id,
                      label:
                        a.description.length > 30
                          ? a.description.slice(0, 30) + '…'
                          : a.description,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel required>Initiated By</FieldLabel>
          <Controller
            control={control}
            name="invoked_by"
            render={({ field }) => <Input {...field} placeholder="Name of person who stopped work" />}
          />
          <FieldError message={errors.invoked_by?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Safety Concern / Hazard</FieldLabel>
          <Controller
            control={control}
            name="hazard_description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={500}
                placeholder="Describe the hazard that triggered the stop"
              />
            )}
          />
          <FieldError message={errors.hazard_description?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Immediate Actions Taken</FieldLabel>
          <Controller
            control={control}
            name="action_taken"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={500}
                placeholder="What was done immediately after stopping work?"
              />
            )}
          />
          <FieldError message={errors.action_taken?.message} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel>Work Crew</FieldLabel>
            <Controller
              control={control}
              name="work_crew"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} placeholder="Crew involved" />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Personnel Affected</FieldLabel>
            <Controller
              control={control}
              name="personnel_count"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="0"
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel required>Severity</FieldLabel>
          <Controller
            control={control}
            name="severity"
            render={({ field }) => (
              <ChipGroup
                options={SEVERITY_OPTIONS}
                value={field.value as SWASeverity}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.severity?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Category (select all that apply)</FieldLabel>
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <MultiChipGroup
                options={CATEGORY_OPTIONS}
                value={field.value as string[]}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.category?.message as string | undefined} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Work Status</FieldLabel>
          <Controller
            control={control}
            name="work_status"
            render={({ field }) => (
              <ChipGroup
                options={WORK_STATUS_OPTIONS}
                value={field.value as SWAWorkStatus}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.work_status?.message} />
        </div>

        {workStatus === 'resumed' && (
          <>
            <div className="space-y-2">
              <FieldLabel required>Resolution / Outcome</FieldLabel>
              <Controller
                control={control}
                name="outcome"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={3}
                    maxLength={500}
                    placeholder="How was the issue resolved before resuming work?"
                  />
                )}
              />
              <FieldError message={errors.outcome?.message} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel required>Authorized By</FieldLabel>
                <Controller
                  control={control}
                  name="authorized_resumption_by"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''} placeholder="Name / role" />
                  )}
                />
                <FieldError message={errors.authorized_resumption_by?.message} />
              </div>
              <div className="space-y-2">
                <FieldLabel required>Resumption Time</FieldLabel>
                <Controller
                  control={control}
                  name="resumption_time"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''} type="datetime-local" />
                  )}
                />
                <FieldError message={errors.resumption_time?.message} />
              </div>
            </div>
          </>
        )}

        <div className="space-y-2">
          <Controller
            control={control}
            name="is_supervisor_notified"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Supervisor Notified
              </label>
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Supervisor Name</FieldLabel>
          <Controller
            control={control}
            name="supervisor_name"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} placeholder="Name of supervisor" />
            )}
          />
        </div>

        <div className="space-y-2">
          <Controller
            control={control}
            name="is_management_notified"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Management Notified
              </label>
            )}
          />
        </div>

        {hasJSAs && (
          <div className="space-y-2">
            <FieldLabel>Linked JSA Evaluation</FieldLabel>
            <Controller
              control={control}
              name="jsa_evaluation_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...jsaEvaluations.map((j) => ({
                      value: j.id,
                      label:
                        j.jsa_reference.length > 30
                          ? j.jsa_reference.slice(0, 30) + '…'
                          : j.jsa_reference,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel>Lessons Learned</FieldLabel>
          <Controller
            control={control}
            name="lessons_learned"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={2}
                maxLength={500}
                placeholder="Key lessons or follow-ups"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Event'
            ) : (
              'Save Event'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
